import React, { useCallback, useMemo, useState } from "react"
import styled from "styled-components"
import { BuyMoreConfiguration, SidebarSection } from "../../../branding/branding"
import Button from "../../../components/Button"
import ModalQrCode from "../../../components/Modal/ModalQrCode"
import { loadQrCodeInfo, useConfigDataState } from "../../../globalStates/ConfigDataState"
import { useExhibitorDataState } from "../../../globalStates/ExhibitorDataState"
import { QrCodeEntityType, QrCodeResult } from "../../../globalStates/ExhibitorDataTypes"
import { useLanguageState } from "../../../globalStates/LanguageState"
import { useReferenceDataState } from "../../../globalStates/ReferenceDataState"
import {
    categoriesPageRoute,
    couponsPageRoute,
    eventDatesPageRoute,
    exhibitorNewsPageRoute,
    homePageRoute,
    jobsPageRoute,
    licencesPageRoute,
    linksPageRoute,
    multimediaPageRoute,
    notificationTemplatesPageRoute,
    organizerNewsPageRoute,
    printPageRoute,
    productsForCollectionsPageRoute,
    productsPageRoute,
    profilePageRoute,
    showroomsPageRoute,
    storyNewsPageRoute,
    teamPageRoute,
    trademarksPageRoute
} from "../../../utils/RoutePaths"
import { isCategoryCountable, selectedCategoriesToMap } from "../../categories/CategoriesContents"

const TopBarActionsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
    max-width: 970px;

    & button {
        margin: 0 5px;
        font-size: 12px;
        line-height: 14px;
        text-align: center;
    }
`

export const TitleWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    & button {
        width: 100px;
        margin-left: 25px;
    }
`

export const Title = styled.div`
    font-weight: bold;
    font-size: 18px;
    line-height: 22px;
`

const TopBarActions: React.FC<{}> = () => {
    const branding = useLanguageState().getStrings()

    const exhibitorState = useExhibitorDataState()
    const organization = exhibitorState.getOrganization()
    const refDataState = useReferenceDataState()
    const serviceKeys = useConfigDataState().getServiceKeys()

    const selectedCategoriesStates = exhibitorState.getCategories()
    const selectedCategories = useMemo(() => selectedCategoriesToMap(selectedCategoriesStates.get()), [selectedCategoriesStates])

    const couponsStates = exhibitorState.getCoupons()
    const notificationTemplatesStates = exhibitorState.getNotificationTemplates()

    const [qrCodeModalVisible, setQrCodeModalVisible] = useState<boolean>(false)
    const [qrCodeUrl, setQrCodeUrl] = useState<string | undefined>()

    // Helper functions
    const getPageName = (): string => {
        switch (window.location.pathname) {
            case homePageRoute:
            case profilePageRoute:
                return branding.configuration.sidebar.lblProfile
            case categoriesPageRoute:
                return branding.configuration.sidebar.lblCategories
            case licencesPageRoute:
                return branding.configuration.sidebar.lblLicences
            case linksPageRoute:
                return branding.configuration.sidebar.lblLinks
            case productsPageRoute:
                return branding.configuration.sidebar.lblProducts
            case productsForCollectionsPageRoute:
                return branding.configuration.sidebar.lblProductsForCollections
            case trademarksPageRoute:
                return branding.configuration.sidebar.lblTrademarks
            case teamPageRoute:
                return branding.configuration.sidebar.lblTeam
            case multimediaPageRoute:
                return branding.configuration.sidebar.lblMultimedia
            case exhibitorNewsPageRoute:
                return branding.configuration.sidebar.lblExhibitorNews
            case organizerNewsPageRoute:
                return branding.configuration.sidebar.lblOrganizerNews
            case storyNewsPageRoute:
                return branding.configuration.sidebar.lblStoryNews
            case eventDatesPageRoute:
                return branding.configuration.sidebar.lblEventDate
            case showroomsPageRoute:
                return branding.configuration.sidebar.lblShowrooms
            case couponsPageRoute:
                return branding.configuration.sidebar.lblCoupons
            case jobsPageRoute:
                return branding.configuration.sidebar.lblJobs
            case printPageRoute:
                return branding.configuration.sidebar.lblPrint
            case notificationTemplatesPageRoute:
                return branding.configuration.sidebar.lblNotificationTemplates
            default:
                return ""
        }
    }

    const getPageKey = (): SidebarSection => {
        switch (window.location.pathname) {
            case homePageRoute:
            case profilePageRoute:
                return SidebarSection.PROFILE
            case categoriesPageRoute:
                return SidebarSection.CATEGORY
            case licencesPageRoute:
                return SidebarSection.LICENCES
            case linksPageRoute:
                return SidebarSection.LINKS
            case productsPageRoute:
                return SidebarSection.PRODUCTS
            case productsForCollectionsPageRoute:
                return SidebarSection.PRODUCTS_FOR_COLLECTIONS
            case trademarksPageRoute:
                return SidebarSection.TRADEMARKS
            case teamPageRoute:
                return SidebarSection.TEAM
            case multimediaPageRoute:
                return SidebarSection.MULTIMEDIA
            case exhibitorNewsPageRoute:
                return SidebarSection.EXHIBITOR_NEWS
            case organizerNewsPageRoute:
                return SidebarSection.ORGANIZER_NEWS
            case storyNewsPageRoute:
                return SidebarSection.STORY_NEWS
            case eventDatesPageRoute:
                return SidebarSection.EVENTDATES
            case showroomsPageRoute:
                return SidebarSection.SHOWROOMS
            case couponsPageRoute:
                return SidebarSection.COUPONS
            case jobsPageRoute:
                return SidebarSection.JOBS
            case printPageRoute:
                return SidebarSection.PRINT
            case notificationTemplatesPageRoute:
                return SidebarSection.NOTIFICATION_TEMPLATES
            default:
                return SidebarSection.PROFILE
        }
    }

    const getSelectionsNumber = (): number => {
        switch (window.location.pathname) {
            case categoriesPageRoute:
                return countSelectedCategories()
            case linksPageRoute:
                return exhibitorState.getLinks().length
            case productsPageRoute:
                return exhibitorState.getProducts().length
            case productsForCollectionsPageRoute:
                return exhibitorState.getProductsForCollections().length
            case trademarksPageRoute:
                return exhibitorState.getTrademarks().length
            case teamPageRoute:
                return exhibitorState.getPersons().length
            case multimediaPageRoute:
                return exhibitorState.getMedias().length
            case exhibitorNewsPageRoute:
                return exhibitorState.getExhibitorNews().length
            case storyNewsPageRoute:
                return exhibitorState.getStoryNews().length
            case showroomsPageRoute:
                return exhibitorState.getShowrooms().length
            case couponsPageRoute:
                return countCoupons()
            case jobsPageRoute:
                return exhibitorState.getJobs().length
            case notificationTemplatesPageRoute:
                return exhibitorState.getNotificationTemplates().length
            default:
                return 0
        }
    }

    const getLimit = (): number => {
        switch (window.location.pathname) {
            case categoriesPageRoute:
                return serviceKeys.category || 0
            case linksPageRoute:
                return serviceKeys.link || 0
            case productsPageRoute:
                return serviceKeys.products || 0
            case productsForCollectionsPageRoute:
                return serviceKeys.productsForCollections || 0
            case trademarksPageRoute:
                return serviceKeys.trademarks || 0
            case teamPageRoute:
                return serviceKeys.contactPartner || 0
            case multimediaPageRoute:
                return serviceKeys.multimedia || 0
            case exhibitorNewsPageRoute:
                return serviceKeys.news || 0
            case storyNewsPageRoute:
                return serviceKeys.stories || 0
            case showroomsPageRoute:
                return (serviceKeys.showroomPrivate || 0) + (serviceKeys.showroomPublic || 0)
            case couponsPageRoute:
                return serviceKeys.goodies || 0
            case jobsPageRoute:
                return serviceKeys.jobAd || 0
            case notificationTemplatesPageRoute:
                return (
                    (serviceKeys["notificationTemplate#small"] || 0) +
                    (serviceKeys["notificationTemplate#medium"] || 0) +
                    (serviceKeys["notificationTemplate#large"] || 0)
                )
            default:
                return 0
        }
    }

    const getBuyMoreLink = (): string | null => {
        const buyMoreConfig = branding.configuration.topBar.buyMoreConfigs.find(
            (config: BuyMoreConfiguration) => config.page === getPageKey()
        )

        if (buyMoreConfig) {
            return buyMoreConfig.link
        }

        return null
    }

    const showCounter = (): boolean => {
        return getLimit() > 0 && branding.showCounter
    }

    //Handling categories
    const countSelectedCategories = (): number => {
        let counter = 0
        const countableCategories = branding.configuration.countableCategories.categoriesCountableCategories
        selectedCategories.forEach((_, selectedId) => {
            const rootId = refDataState.findRootByCategoryId(selectedId)?.id ?? ""
            if (isCategoryCountable(countableCategories, rootId)) counter++
        })
        return counter
    }

    const countCoupons = (): number => {
        const filteredCouponsStates = couponsStates.filter(
            (coupon) =>
                !(
                    notificationTemplatesStates.filter((notification) => notification.action.get().param === coupon.id.get())
                        .length === 0
                )
        )

        return couponsStates.length - filteredCouponsStates.length
    }

    //Action buttons
    const showBuyMore = (): boolean => {
        return getLimit() > 0 && getSelectionsNumber() >= getLimit() && getBuyMoreLink() !== null
    }

    const buyMore = () => {
        if (getBuyMoreLink()) {
            window.open(getBuyMoreLink() || "", "_blank")
        }
    }

    const onClickQrCode = useCallback(() => {
        loadQrCodeInfo(QrCodeEntityType.ORGANIZATION, organization.id.get())
            .then((response: QrCodeResult) => {
                setQrCodeUrl(response.url)
                setQrCodeModalVisible(true)
            })
            .catch((error: any) => {
                setQrCodeUrl(undefined)
                setQrCodeModalVisible(true)
            })
    }, [organization])

    return (
        <TopBarActionsWrapper>
            <TitleWrapper>
                <Title>{`${getPageName()} ${
                    showCounter() ? `(${String(getSelectionsNumber())}/${String(getLimit())})` : ""
                }`}</Title>
                {showBuyMore() && (
                    <Button
                        id={`buymore-${getPageKey()}`}
                        caption={branding.configuration.topBar.buyMoreButtonText}
                        inverse={true}
                        onClick={buyMore}
                    />
                )}
            </TitleWrapper>
            {branding.configuration.qrCodes.enabled &&
                (window.location.pathname === homePageRoute || window.location.pathname === profilePageRoute) && (
                    <Button
                        id="downloadqrcode"
                        caption={branding.configuration.topBar.downloadQrCodeButtonText}
                        inverse={true}
                        onClick={onClickQrCode}
                    />
                )}

            {qrCodeModalVisible && (
                <ModalQrCode
                    entityType={QrCodeEntityType.ORGANIZATION}
                    name={organization.name.get()}
                    url={qrCodeUrl}
                    visible={qrCodeModalVisible}
                    onClose={() => {
                        setQrCodeModalVisible(false)
                    }}
                />
            )}
        </TopBarActionsWrapper>
    )
}

export default TopBarActions
